// Deploying a package of templates to a set of locations in one motion. A workflow belongs to exactly
// one location (trackable-api #530), so "these four templates at these five locations" is twenty
// workflows, and this module is the loop the chooser runs instead of asking a super for twenty
// trips through the builder.
import type { Workflow } from 'app/generated/workflows'
import { listCopy } from 'lib/listCopy'
import { nodeGates } from './blockGates'
import type { BlockedGate, GateLocation, GateToggles } from 'app/types/workflow-gates'
import { serializeWorkflow } from './serialize'
import { isSystemEnrolmentWorkflow } from './systemEnrolment'
import { createRemoteWorkflow, setRemoteWorkflowCategory, updateRemoteWorkflow } from './workflowApi'
import { DEFAULT_WORKFLOW_SETTINGS, categorySyncPlan } from './workflowSettings'
import type { WorkflowTemplate } from './workflowTemplates'

export type DeployLocation = GateLocation

export type DeployTarget = { template: WorkflowTemplate; location: DeployLocation }

export type DeployPlan = {
  targets: DeployTarget[]
  /** Pairs the location already has. Deploying twice would double-write pipeline memberships. */
  skipped: DeployTarget[]
  /** Pairs the location can't run (a product it lacks, no practice system): never created. */
  locked: { target: DeployTarget; gate: BlockedGate }[]
}

export type DeployOutcome = {
  target: DeployTarget
  /** The server id once created. Present even when a later step (activation) failed. */
  workflowId?: string
  error?: string
}

/** The workflows list has no Location column, so the name carries it: "Hygiene recall enrolment ·
 *  Downtown". Middle dot, the house separator. */
export function deployedWorkflowName(template: WorkflowTemplate, location: DeployLocation): string {
  const base = template.build?.().name ?? template.title
  return location.name ? `${base} · ${location.name}` : base
}

export function planDeployment(
  templates: WorkflowTemplate[],
  locations: DeployLocation[],
  existing: Pick<Workflow, `name` | `locationId` | `nodesJson`>[],
  /** Unknown toggles count as released, so a plan made before they load locks nothing extra. */
  toggles: GateToggles = { schedulingAgent: true, paperwork: true },
): DeployPlan {
  const targets: DeployTarget[] = []
  const skipped: DeployTarget[] = []
  const locked: DeployPlan[`locked`] = []
  for (const template of templates) {
    for (const location of locations) {
      const target = { template, location }
      const names = new Set([deployedWorkflowName(template, location), template.build?.().name ?? template.title])
      // Matched on name + location + what it writes, not on name alone: a practice is free to name
      // its own workflow anything, and only one that writes a system pipeline is a real duplicate.
      const already = existing.some(
        (workflow) =>
          workflow.locationId === location.id && names.has(workflow.name) && isSystemEnrolmentWorkflow(workflow),
      )
      const gate = [...nodeGates(template.build?.().nodes ?? [], location, toggles).values()][0]
      if (gate && !already) locked.push({ target, gate })
      else (already ? skipped : targets).push(target)
    }
  }
  return { targets, skipped, locked }
}

export async function deployTemplates(
  targets: DeployTarget[],
  options: { activate: boolean; canSetCategory: boolean; onProgress?: (done: number, total: number) => void },
): Promise<DeployOutcome[]> {
  const outcomes: DeployOutcome[] = []
  // Sequential, not Promise.all: twenty simultaneous creates against one workspace is the kind of
  // burst that turns one transient failure into twenty, and the progress line wants an order.
  for (const target of targets) {
    outcomes.push(await deployOne(target, options))
    options.onProgress?.(outcomes.length, targets.length)
  }
  return outcomes
}

async function deployOne(
  target: DeployTarget,
  { activate, canSetCategory }: { activate: boolean; canSetCategory: boolean },
): Promise<DeployOutcome> {
  const doc = target.template.build?.()
  if (!doc) return { target, error: `This template cannot be built yet.` }

  const settings = { ...DEFAULT_WORKFLOW_SETTINGS, category: doc.messageCategory }
  const { input, blocked, gaps } = serializeWorkflow({
    locationId: target.location.id,
    name: deployedWorkflowName(target.template, target.location),
    nodes: doc.nodes,
    edges: doc.edges,
    settings,
    // This deployer has no builder/component in scope, so there is no live toggle state to read.
    // The pipeline wording is safe here regardless: templates are built fresh from workflowTemplates.ts,
    // whose stage conditions always carry the composite `pipelines.key` value, so the one gap message
    // that reads this record (a bare legacy stage name) can never fire on a freshly-built template.
    copy: listCopy(false),
  })
  if (blocked || !input) return { target, error: gaps.map((gap) => gap.message).join(` `) || `Could not compile.` }

  const created = await createRemoteWorkflow(input)
  if (created.error || !created.data) return { target, error: created.error ?? `The server returned no workflow.` }
  const workflowId = created.data.id
  const problems: string[] = []

  if (activate) {
    const activated = await updateRemoteWorkflow(workflowId, { active: true })
    if (activated.error) problems.push(`created but not activated: ${activated.error}`)
  }

  // Same rule as the builder's save: the engine treats every workflow as marketing until a super
  // marks it otherwise, and only a super's call reaches the ops mutation.
  const plan = categorySyncPlan({ canSetCategory, settings, serverCategory: created.data.category })
  if (plan.apply) {
    const synced = await setRemoteWorkflowCategory(workflowId, plan.category)
    if (synced.error) problems.push(`texting category did not sync: ${synced.error}`)
  }

  return { target, workflowId, error: problems.length ? problems.join(`; `) : undefined }
}
